// src/pages/procurement/components/SupplierStatementPrintModal.jsx
// Cloned from src/pages/offlineSales/components/CustomerStatementPrintModal.jsx — same shared
// PrintPreviewModal with a receipt/A4 toggle and a PDF download button, reusing
// OrderReceiptPrintModal's CSS module (class names are receipt-generic, not order-specific).
import React from 'react';
import { useLocalization } from '../../../contexts/LocalizationContext';
import { exportSupplierStatementPDF } from '../../../utils/offlineOrderPdfExporter';
import { getCssVar } from '../../../utils/themeColors';
import { formatDate } from '../../../utils/dateUtils';
import PrintPreviewModal from '../../../components/common/PrintPreviewModal/PrintPreviewModal';
import SupplierStatementA4 from './SupplierStatementA4';
import styles from '../../offlineSales/components/OrderReceiptPrintModal.module.css';

const TYPE_LABELS = {
  purchase_order: 'Purchase Order',
  supplier_payment: 'Payment',
  debit_note: 'Debit Note',
};

const SupplierStatementPrintModal = ({ statement, onClose }) => {
  const { t, formatCurrency } = useLocalization();

  if (!statement) return null;

  const renderReceipt = () => (
    <div className={styles.receiptArea}>
      <h2 className={styles.receiptTitle}>{t('procurement.supplierStatement')}</h2>
      <p className={styles.receiptShop}>{statement.supplier?.name}</p>

      <div className={styles.receiptMeta}>
        <div className={styles.receiptMetaRow}>
          <span>{t('procurement.statementPeriod')}</span>
          <span>{statement.dateFrom ? formatDate(statement.dateFrom) : t('procurement.accountStart')} — {statement.dateTo ? formatDate(statement.dateTo) : t('procurement.today')}</span>
        </div>
        <div className={styles.receiptMetaRow}>
          <span>{t('procurement.openingBalance')}</span>
          <span>{formatCurrency(statement.openingBalance)}</span>
        </div>
      </div>

      <hr className={styles.receiptDivider} />

      <div className={styles.receiptItems}>
        {(statement.transactions || []).map((tx, index) => (
          <div key={index} className={styles.receiptItem}>
            <span className={styles.receiptItemName}>
              {new Date(tx.date).toLocaleDateString()} — {TYPE_LABELS[tx.type] || tx.type} ({tx.reference || '—'})
            </span>
            <span className={styles.receiptItemTotal}>
              {(tx.debit > 0 || tx.type === 'purchase_order') ? formatCurrency(tx.debit) : `-${formatCurrency(tx.credit)}`}
            </span>
          </div>
        ))}
      </div>

      <hr className={styles.receiptDivider} />

      <div className={styles.receiptTotals}>
        <div className={`${styles.receiptTotalRow} ${styles.receiptGrandTotal}`}>
          <strong>{t('procurement.closingBalance')}</strong>
          <strong>{formatCurrency(statement.closingBalance)}</strong>
        </div>
      </div>
    </div>
  );

  return (
    <PrintPreviewModal
      title={`${t('procurement.supplierStatement')} — ${statement.supplier?.name || ''}`}
      onClose={onClose}
      pdfFileName={`Supplier_Statement_${statement.supplier?.name || "Supplier"}.pdf`}
      onDownloadPdf={() => exportSupplierStatementPDF(statement, {
        primary: getCssVar('--color-primary', '#4f46e5'),
        primaryDark: getCssVar('--color-primary-dark', '#3730a3'),
      })}
      renderReceipt={renderReceipt}
      renderA4={() => <SupplierStatementA4 statement={statement} />}
    />
  );
};

export default SupplierStatementPrintModal;
